import {
    Entity,
    RELATION_OWNED_BY,
    stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';

/** What a service provider offers. */
export type ServiceProviderFacts = {
    /** The services the team owns, sorted by title. */
    services: Entity[];
};

/** How a contact of a team is shown: their name and their photo. */
export type ContactProfile = {
    displayName?: string;
    picture?: string;
};

/** A contact as the CMDB provider puts it on a team. */
type Contact = { entityRef?: string };

/**
 * The user names of the accountable and the main contacts of the teams, the
 * people the cards show.
 */
function contactNamesOf(teams: Entity[]): string[] {
    const names = teams.flatMap(team => {
        const spec = team.spec ?? {};
        const accountable = spec.accountableContact as Contact | undefined;
        const main = (spec.mainContacts as Contact[] | undefined) ?? [];
        return [accountable, ...main].flatMap(contact =>
            contact?.entityRef ? [contact.entityRef] : [],
        );
    });
    return [...new Set(names)].sort();
}

/** User names are compared the way the catalog compares them. */
const userKey = (name: string) => name.toLocaleLowerCase('en-US');

const NO_FACTS: ServiceProviderFacts = { services: [] };

/**
 * What the teams offer and who their contacts are, for all of them at once:
 * the `Service` entities owned by the teams in one request, the names and
 * photos of their contacts in another.
 */
export function useServiceProviderFacts(teams: Entity[]): {
    factsOf: (team: Entity) => ServiceProviderFacts;
    profileOf: (userName: string) => ContactProfile | undefined;
    loading: boolean;
} {
    const catalogApi = useApi(catalogApiRef);
    const refs = teams.map(team => stringifyEntityRef(team)).sort();
    // The list is a fresh array on every render, the requests are only made
    // again when the teams change.
    const refsKey = refs.join(',');
    const contactNames = contactNamesOf(teams);
    const contactsKey = contactNames.join(',');

    const { value, loading } = useAsync(async () => {
        if (refs.length === 0) {
            return undefined;
        }
        const { items } = await catalogApi.getEntities({
            filter: { kind: 'Service', 'relations.ownedBy': refs },
            fields: [
                'kind',
                'metadata.name',
                'metadata.namespace',
                'metadata.title',
                'metadata.description',
                'metadata.links',
                'spec.type',
                'spec.lifecycle',
                'relations',
            ],
        });
        return items;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalogApi, refsKey]);

    // The photos of the people the cards name, one request for all of them and
    // only for what a card shows of them.
    const { value: profiles } = useAsync(async () => {
        if (contactNames.length === 0) {
            return new Map<string, ContactProfile>();
        }
        const { items } = await catalogApi.getEntitiesByRefs({
            entityRefs: contactNames.map(name => `user:default/${name}`),
            fields: [
                'metadata.name',
                'spec.profile.displayName',
                'spec.profile.picture',
            ],
        });
        return new Map(
            items.flatMap(user =>
                user
                    ? [
                          [
                              userKey(user.metadata.name),
                              (user.spec?.profile ?? {}) as ContactProfile,
                          ] as const,
                      ]
                    : [],
            ),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalogApi, contactsKey]);

    const profileOf = (userName: string) => profiles?.get(userKey(userName));

    const factsOf = (team: Entity): ServiceProviderFacts => {
        if (!value) {
            return NO_FACTS;
        }
        const ref = stringifyEntityRef(team);
        const titleOf = (entity: Entity) =>
            entity.metadata.title ?? entity.metadata.name;
        return {
            services: value
                .filter(service =>
                    service.relations?.some(
                        relation =>
                            relation.type === RELATION_OWNED_BY &&
                            relation.targetRef === ref,
                    ),
                )
                .sort((a, b) => titleOf(a).localeCompare(titleOf(b))),
        };
    };

    return { factsOf, profileOf, loading };
}
